import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { BrandMark } from './BrandMark';
import './Footer.css';

type SubscribeStatus = 'idle' | 'submitting' | 'success' | 'error';

export function Footer() {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState<SubscribeStatus>('idle');
  const [subscribeError, setSubscribeError] = useState('');

  async function handleSubscribe(e: FormEvent) {
    e.preventDefault();
    setSubscribeStatus('submitting');
    setSubscribeError('');
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, consent }),
      });
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Service unavailable. Please try again later.');
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
      setSubscribeStatus('success');
    } catch (err) {
      setSubscribeStatus('error');
      setSubscribeError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div className="footer__brand">
            <Link to="/" className="footer__logo">Notation of Being</Link>
            <p className="footer__tagline">
              Understanding the frames we inherit — and learning how to author our own.
            </p>
          </div>

          <div className="footer__links">
            <h4 className="footer__heading">Explore</h4>
            <ul>
              <li><Link to="/about">About</Link></li>
              <li><Link to="/book">Book</Link></li>
              <li><span className="footer__coming-soon">Podcast (Soon)</span></li>
              <li><Link to="/blog">Blog</Link></li>
            </ul>
          </div>

          <div className="footer__links">
            <h4 className="footer__heading">Connect</h4>
            <ul>
              <li>
                <a href="https://www.instagram.com/notationofbeing/" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-instagram"></i> Instagram
                </a>
              </li>
              <li>
                <a href="https://www.facebook.com/profile.php?id=61587944112457" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-facebook"></i> Facebook
                </a>
              </li>
              <li>
                <a href="https://x.com/SpencerDav77198" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-x-twitter"></i> X
                </a>
              </li>
              <li>
                <span className="footer__coming-soon">
                  <i className="fab fa-youtube"></i> YouTube (Soon)
                </span>
              </li>
            </ul>
          </div>

          <div className="footer__newsletter">
            <h4 className="footer__heading">Stay Updated</h4>
            <p>Subscribe for new content and updates.</p>
            {subscribeStatus === 'success' ? (
              <p className="footer__newsletter-status footer__newsletter-status--success">
                <i className="fas fa-check-circle"></i> You're subscribed — thank you!
              </p>
            ) : (
              <form className="footer__subscribe-form" onSubmit={handleSubscribe}>
                <div className="footer__form">
                  <input
                    className="footer__input"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    aria-label="Email address"
                  />
                  <button
                    type="submit"
                    className="footer__submit"
                    disabled={subscribeStatus === 'submitting' || !consent}
                    aria-label="Subscribe"
                  >
                    {subscribeStatus === 'submitting'
                      ? <i className="fas fa-spinner fa-spin"></i>
                      : <i className="fas fa-arrow-right"></i>}
                  </button>
                </div>
                <div className="footer__consent">
                  <input
                    id="footer-consent"
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                    required
                  />
                  <label htmlFor="footer-consent" className="footer__consent-label">
                    By subscribing, you agree to receive email updates from Notation of Being.
                    You may unsubscribe at any time. See our{' '}
                    <Link to="/privacy">Privacy Policy</Link> for details.
                  </label>
                </div>
                {subscribeStatus === 'error' && (
                  <p className="footer__newsletter-status footer__newsletter-status--error">
                    <i className="fas fa-exclamation-circle"></i> {subscribeError}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>

        <div className="footer__bottom">
          <BrandMark size={48} className="footer__bottom-mark" />
          <p>&copy; {currentYear} Notation of Being LLC. All rights reserved.</p>
          <p className="footer__bottom-legal">
            <Link to="/privacy">Privacy Policy</Link>
            <span className="footer__bottom-sep">&bull;</span>
            <Link to="/terms">Terms of Use</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
